import React, { useMemo } from "react";
import { useLanguage } from "../Language";
import { PlaceHoldInfoImpl } from "../services/Holds";
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useIsMobile } from "@tlc/ui/useIsMobile";
import { useFailedHoldMessage } from "../useFailedHoldMessage";
import { ResourceView } from "../../dto/ResourceView";
import { ResourceDWR } from "../../dto/ResourceDWR";

interface PlaceHoldResponseDialogProps {
  open: boolean;
  onClose: () => void;
  response: PlaceHoldInfoImpl[];
  resource: ResourceView | ResourceDWR;
}
export const PlaceHoldResponseDialog = ({
  open,
  onClose,
  response,
  resource,
}: PlaceHoldResponseDialogProps) => {
  const { translate } = useLanguage();
  const failedHoldMessage = useFailedHoldMessage();
  const isMobile = useIsMobile();

  //   TODO use a map instead?
  const groupedFailedHolds = useMemo(() => {
    if (!response) {
      return {};
    }
    return response
      .filter(({ success }) => !success)
      .reduce((prev, current) => {
        const { message: token, guardiansBlocking } = current;
        const message = failedHoldMessage(token, guardiansBlocking);
        if (!prev[message]) {
          prev[message] = [current];
        } else {
          prev[message] = [...prev[message], current];
        }
        return prev;
      }, {} as { [token: string]: PlaceHoldInfoImpl[] });
  }, [response]);

  const successfulHolds = useMemo(() => {
    if (!response) return [];
    return response.filter(({ success }) => success);
  }, [response]);

  const getHoldListItemText = (
    { bibliographicId, titleLevelHold, serialItem }: PlaceHoldInfoImpl,
    resource: ResourceView | ResourceDWR,
  ) => {
    if (titleLevelHold) {
      return {
        primary: <>resource.shortTitle</>,
        secondary: <>resource.shortAuthor</>,
      };
    }

    const holding = resource.holdingsInformations.find(
      (hi) => hi.barcode === bibliographicId,
    );

    if (!holding) {
      console.error(`no matching holding found for ${bibliographicId}`);
      return {
        primary: <>bibliographicId</>,
      };
    }
    if (serialItem) {
      const { chronology, enumeration, branchName, collectionName } = holding;
      return {
        primary: `${chronology}; ${enumeration}`,
        secondary: `${branchName}; ${collectionName}`,
      };
    }

    const { shelfLocation, branchName, collectionName } = holding;
    return {
      primary: shelfLocation,
      secondary: `${branchName}; ${collectionName}`,
    };
  };

  const getHoldItemDisplay = (
    holdInfo: PlaceHoldInfoImpl,
    resource: ResourceView | ResourceDWR,
  ) => {
    const { bibliographicId } = holdInfo;
    const { primary, secondary } = getHoldListItemText(holdInfo, resource);

    return (
      <ListItem disableGutters disablePadding dense key={bibliographicId}>
        <ListItemText primary={primary} secondary={secondary} />
      </ListItem>
    );
  };

  return (
    <Dialog fullScreen={isMobile} open={open} onClose={onClose}>
      <DialogTitle>{translate("errorDialogTitle")}</DialogTitle>
      <DialogContent>
        {successfulHolds.length > 0 && (
          <Alert severity="success">
            <AlertTitle>{translate("batchRequestSuccessMessage")}</AlertTitle>
            <List disablePadding dense>
              {successfulHolds.map((hi) => getHoldItemDisplay(hi, resource))}
            </List>
          </Alert>
        )}
        <Typography>{translate("batchRequestFailureMessage")}</Typography>
        {Object.entries(groupedFailedHolds).map(([message, holdInfoList]) => {
          return (
            <Alert key={message} severity="error">
              <AlertTitle>{message}</AlertTitle>
              <List dense disablePadding>
                {holdInfoList.map((hi) => getHoldItemDisplay(hi, resource))}
              </List>
            </Alert>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate("ok")}</Button>
      </DialogActions>
    </Dialog>
  );
};
